/**
 * Identity module request-error helpers.
 *
 * One shared, side-effect-free place to turn a non-2xx response into a typed,
 * English, secret-free error. Every identity API file imports this so error
 * mapping is identical across sign-in, register, password and PIN, and so no
 * request body (password, PIN, token) can ever leak into a message.
 *
 * This is not an endpoint wrapper: each API operation still lives in its own
 * file with its own response type.
 */

/** Field-level validation message returned by the API error envelope. */
export interface ApiFieldError {
  field: string;
  code: string;
  message: string;
}

/** The error envelope shared by every module (see shared/components.yaml). */
interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
    fieldErrors?: ApiFieldError[];
  };
}

/**
 * Typed transport error. `code` is the stable server error code when present;
 * `fieldErrors` carries per-field validation messages; `retryAfterSeconds` is
 * populated for throttled (429) responses when the server reports it.
 */
export class IdentityRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: ApiFieldError[];
  readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    status: number,
    code = "",
    fieldErrors: ApiFieldError[] = [],
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "IdentityRequestError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** True when the request was refused for missing/expired authentication. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True when the request was throttled. */
  get isThrottled(): boolean {
    return this.status === 429;
  }
}

function readRetryAfter(details: Record<string, unknown> | undefined): number | null {
  const value = details?.retryAfterSeconds;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Reads an error response into an IdentityRequestError. Only the server's own
 * safe English message is used; the raw body is never surfaced, and a body that
 * is not JSON falls back to a status-only message.
 */
export async function toRequestError(response: Response): Promise<IdentityRequestError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    const envelope = body?.error;
    if (envelope?.message) {
      return new IdentityRequestError(
        envelope.message,
        response.status,
        envelope.code ?? "",
        Array.isArray(envelope.fieldErrors) ? envelope.fieldErrors : [],
        readRetryAfter(envelope.details),
      );
    }
  } catch {
    // Non-JSON or empty body: there is no safe server message to reuse.
  }
  return new IdentityRequestError(
    `Request failed with status ${response.status}.`,
    response.status,
  );
}
